import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, Eye, Clock, MapPin, Share2, Facebook, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";

const categoryLabels = {
  general: { label: "Historia", color: "bg-blue-100 text-blue-800" },
  "antes-despues": { label: "Antes / Después", color: "bg-purple-100 text-purple-800" },
  tradicion: { label: "Tradición", color: "bg-orange-100 text-orange-800" },
  personaje: { label: "Personaje", color: "bg-green-100 text-green-800" },
  oficio: { label: "Oficio", color: "bg-yellow-100 text-yellow-800" },
};

function BeforeAfterSlider({ beforeUrl, afterUrl, title }) {
  const [sliderPosition, setSliderPosition] = useState(50);

  return (
    <div className="relative aspect-video rounded-3xl overflow-hidden select-none">
      {/* After Image (background) */}
      <img
        src={afterUrl}
        alt="Después"
        className="absolute inset-0 w-full h-full object-cover"
      />
      
      {/* Before Image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeUrl}
          alt="Antes"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ width: `${100 / (sliderPosition / 100)}%` }}
        />
      </div>

      {/* Slider */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize z-10"
        style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
        onMouseDown={(e) => {
          const handleMove = (moveEvent) => {
            const rect = e.target.parentElement.getBoundingClientRect();
            const x = moveEvent.clientX - rect.left;
            const percentage = Math.min(Math.max((x / rect.width) * 100, 5), 95);
            setSliderPosition(percentage);
          };
          const handleUp = () => {
            document.removeEventListener("mousemove", handleMove);
            document.removeEventListener("mouseup", handleUp);
          };
          document.addEventListener("mousemove", handleMove);
          document.addEventListener("mouseup", handleUp);
        }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <div className="flex gap-0.5">
            <div className="w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-400" />
            <div className="w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent border-l-gray-400" />
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute bottom-4 left-4 px-3 py-1 bg-black/70 text-white text-sm rounded-full">
        Antes
      </div>
      <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/70 text-white text-sm rounded-full">
        Después
      </div>
    </div>
  );
}

export default function MemoriaDetailPage() {
  const { slug } = useParams();
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState(false);

  useEffect(() => {
    const fetchMemory = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/memories/${slug}`);
        setMemory(response.data);
      } catch (error) {
        console.error("Error fetching memory:", error);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchMemory();
  }, [slug]);

  const handleVote = async () => {
    if (voted) return;
    try {
      const formData = new FormData();
      formData.append("vote_type", "up");
      const response = await axios.post(
        `${API}/vote/memory/${memory.memory_id}`,
        formData
      );
      setMemory({ ...memory, vote_count: response.data.vote_count });
      setVoted(true);
      toast.success("¡Gracias por tu voto!");
    } catch (error) {
      toast.error("Error al votar");
    }
  };

  const handleShare = (platform) => {
    const shareUrl = window.location.href;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(memory?.title || "")}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${memory?.title} - ${shareUrl}`)}`,
    };
    window.open(urls[platform], "_blank", "width=600,height=400");
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-gray-200 rounded mb-8" />
            <div className="h-12 w-3/4 bg-gray-200 rounded mb-4" />
            <div className="aspect-video bg-gray-200 rounded-3xl mb-8" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!memory) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-600 mb-4">
            Memoria no encontrada
          </h1>
          <Link to="/memorias">
            <Button className="rounded-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Memorias
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const catInfo = categoryLabels[memory.category] || categoryLabels.general;
  const placeholderImage = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=85";

  // Helper to resolve image URLs
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (url.startsWith('/uploads/')) {
      return `${backendUrl}/api${url}`;
    }
    return `${backendUrl}${url}`;
  };

  const featuredImage = getImageUrl(memory.featured_image_url);
  const beforeImage = getImageUrl(memory.before_image_url);
  const afterImage = getImageUrl(memory.after_image_url);

  return (
    <Layout>
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="memory-detail">
        {/* Back Link */}
        <Link
          to="/memorias"
          className="inline-flex items-center text-brand-textSecondary hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver a Memorias
        </Link>

        {/* Meta */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center gap-3 mb-4"
        >
          <Badge className={`${catInfo.color} border-0`}>{catInfo.label}</Badge>
          {memory.year_approx && (
            <span className="flex items-center gap-1 text-brand-textMuted text-sm">
              <Clock className="h-4 w-4" />
              {memory.year_approx}
            </span>
          )}
          {memory.municipio && (
            <span className="flex items-center gap-1 text-brand-textMuted text-sm">
              <MapPin className="h-4 w-4" />
              {memory.municipio}
            </span>
          )}
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-brand-text leading-tight mb-6"
        >
          {memory.title}
        </motion.h1>

        {/* Author & Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap items-center gap-4 text-brand-textSecondary text-sm mb-8 pb-8 border-b border-gray-200"
        >
          {memory.author_name && (
            <span className="font-medium text-brand-text">
              Por {memory.author_name}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {memory.view_count || 0} vistas
          </span>
          <span className="flex items-center gap-1">
            <Heart className="h-4 w-4" />
            {memory.vote_count || 0} votos
          </span>
        </motion.div>

        {/* Before/After or Featured Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-10"
        >
          {beforeImage && afterImage ? (
            <BeforeAfterSlider
              beforeUrl={beforeImage}
              afterUrl={afterImage}
              title={memory.title}
            />
          ) : featuredImage ? (
            <img
              src={featuredImage}
              alt={memory.title}
              className="w-full aspect-video object-cover rounded-3xl"
            />
          ) : null}
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="prose-content max-w-none"
          dangerouslySetInnerHTML={{ __html: memory.content_html || "" }}
        />

        {/* Gallery */}
        {memory.gallery_urls && memory.gallery_urls.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-10"
          >
            <h3 className="font-heading text-xl font-bold text-brand-text mb-4">
              Galería de fotos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {memory.gallery_urls.map((img, idx) => (
                <img
                  key={idx}
                  src={getImageUrl(img.url)}
                  alt={`Foto ${idx + 1}`}
                  className="w-full aspect-square object-cover rounded-2xl"
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Videos */}
        {memory.video_urls && memory.video_urls.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-10"
          >
            <h3 className="font-heading text-xl font-bold text-brand-text mb-4">
              Videos relacionados
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {memory.video_urls.map((url, idx) => {
                const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
                return videoId ? (
                  <div key={idx} className="aspect-video rounded-2xl overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}`}
                      className="w-full h-full"
                      allowFullScreen
                      title={`Video ${idx + 1}`}
                    />
                  </div>
                ) : null;
              })}
            </div>
          </motion.div>
        )}

        {/* Vote & Share */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mt-12 pt-8 border-t border-gray-200"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Vote */}
            <Button
              onClick={handleVote}
              disabled={voted}
              variant={voted ? "secondary" : "default"}
              className="rounded-full"
              data-testid="vote-btn"
            >
              <Heart className={`h-5 w-5 mr-2 ${voted ? "fill-current" : ""}`} />
              {voted ? "¡Votaste!" : "Me gusta esta memoria"}
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-sm">
                {memory.vote_count || 0}
              </span>
            </Button>

            {/* Share */}
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 text-brand-textSecondary">
                <Share2 className="h-5 w-5" />
                Compartir:
              </span>
              <button
                onClick={() => handleShare("facebook")}
                className="share-btn facebook"
                aria-label="Compartir en Facebook"
              >
                <Facebook className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleShare("twitter")}
                className="share-btn twitter"
                aria-label="Compartir en Twitter"
              >
                <Twitter className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleShare("whatsapp")}
                className="share-btn whatsapp"
                aria-label="Compartir en WhatsApp"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </button>
            </div>
          </div>
        </motion.div>
      </article>
    </Layout>
  );
}
